import { useMemo } from 'react'
import { parse as parseYaml } from 'yaml'
import { Badge } from '@/components/ui/badge'

interface TriggerInputsDisplayProps {
  yaml: string
}

interface TriggerInput {
  name: string
  type: string
  required: boolean
}

function extractTriggerInputs(yamlContent: string): TriggerInput[] {
  if (!yamlContent.trim()) return []
  try {
    const parsed = parseYaml(yamlContent)
    const inputs = parsed?.trigger?.inputs
    if (!inputs || typeof inputs !== 'object') return []

    return Object.entries(inputs).map(([name, config]) => {
      const cfg = config as Record<string, unknown>
      return {
        name,
        type: String(cfg?.type ?? 'string'),
        required: cfg?.required === true,
      }
    })
  } catch {
    return []
  }
}

export function TriggerInputsDisplay({ yaml }: TriggerInputsDisplayProps) {
  const inputs = useMemo(() => extractTriggerInputs(yaml), [yaml])

  if (inputs.length === 0) return null

  return (
    <div className="border-t p-3">
      <h4 className="text-sm font-semibold mb-2">Trigger Inputs</h4>
      <div className="flex flex-wrap gap-1.5">
        {inputs.map((input) => (
          <Badge key={input.name} variant="outline" className="text-xs gap-1 border-muted-foreground">
            {input.name}
            <span className="text-muted-foreground">({input.type})</span>
            {input.required && (
              <span className="text-red-500">*</span>
            )}
          </Badge>
        ))}
      </div>
    </div>
  )
}
